import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ArrowLeft, Check } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { productsAPI, brandsAPI } from '../services/api';
import { usePageView } from '../hooks/useAnalytics';

const Catalog = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Para extrair cores
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Track page view
  usePageView('catalog');
  
  const [filters, setFilters] = useState({
    brand: '',
    condition: '',
    minPrice: '',
    maxPrice: '',
    search: '',
    color: '' // Novo filtro de cor
  });

  // Extrair todas as cores únicas dos produtos
  const availableColors = useMemo(() => {
    const colorsMap = new Map();
    
    allProducts.forEach(product => {
      let variants = [];
      if (product.color_variants) {
        try {
          variants = typeof product.color_variants === 'string' 
            ? JSON.parse(product.color_variants) 
            : product.color_variants;
        } catch {
          variants = [];
        }
      }
      
      variants.forEach(variant => {
        if (variant.id && variant.name && variant.hex) {
          // Usa o ID como chave para evitar duplicatas
          if (!colorsMap.has(variant.id)) {
            colorsMap.set(variant.id, {
              id: variant.id,
              name: variant.name,
              hex: variant.hex
            });
          }
        }
      });
    });
    
    return Array.from(colorsMap.values());
  }, [allProducts]);

  const [isInitialized, setIsInitialized] = useState(false);

  // Ler parâmetros da URL na primeira carga
  useEffect(() => {
    const brandFromUrl = searchParams.get('brand');
    const colorFromUrl = searchParams.get('color');
    
    setFilters(prev => ({
      ...prev,
      brand: brandFromUrl || '',
      color: colorFromUrl || ''
    }));
    
    fetchBrands();
    fetchAllProducts(); // Busca todos para extrair cores
    setIsInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    // Só busca produtos após inicialização para evitar race condition
    if (isInitialized) {
      fetchProducts();
    }
  }, [filters, isInitialized]);

  const fetchBrands = async () => {
    try {
      const response = await brandsAPI.getAll();
      setBrands(response.data.brands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await productsAPI.getAll({});
      setAllProducts(response.data);
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Busca produtos com os filtros básicos (sem cor, pois filtramos no frontend)
      const { color, ...basicFilters } = filters;
      const response = await productsAPI.getAll(basicFilters);
      let filteredProducts = response.data;
      
      // Filtra por cor no frontend
      if (color) {
        filteredProducts = filteredProducts.filter(product => {
          let variants = [];
          if (product.color_variants) {
            try {
              variants = typeof product.color_variants === 'string' 
                ? JSON.parse(product.color_variants) 
                : product.color_variants;
            } catch {
              variants = [];
            }
          }
          return variants.some(v => v.id === color && v.available !== false);
        });
      }
      
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleColorSelect = (colorId) => {
    // Toggle: se já está selecionada, remove; senão, seleciona
    setFilters(prev => ({
      ...prev,
      color: prev.color === colorId ? '' : colorId
    }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      condition: '',
      minPrice: '',
      maxPrice: '',
      search: '',
      color: ''
    });
  };

  // Verifica se tem filtros ativos
  const hasActiveFilters = filters.brand || filters.condition || filters.minPrice || filters.maxPrice || filters.search || filters.color;

  // Conta filtros ativos
  const activeFiltersCount = [filters.brand, filters.condition, filters.minPrice, filters.maxPrice, filters.color].filter(Boolean).length;

  // Nome da cor selecionada
  const selectedColorName = filters.color 
    ? availableColors.find(c => c.id === filters.color)?.name 
    : null;

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      {/* Botão Voltar - Mobile */}
      <div className="md:hidden bg-[#1C1C1F] border-b border-[#2A2A2D] px-4 py-3">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#C9A24D] hover:text-[#E0B85C] transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Voltar ao Início</span>
        </Link>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#1C1C1F] to-[#0F0F10] text-white py-16 border-b border-[#2A2A2D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-cinzel text-5xl font-bold mb-4 text-metallic tracking-wider">
            {filters.brand ? `${filters.brand.toUpperCase()}` : 'CATÁLOGO PREMIUM'}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-[#C9A24D] to-transparent mb-4"></div>
          <p className="text-[#B5B5B5] text-lg">
            {filters.brand 
              ? `Produtos ${filters.brand} de alto padrão` 
              : selectedColorName
                ? `Produtos na cor ${selectedColorName}`
                : 'Encontre o celular premium ideal para você'
            }
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="mb-8 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B5B5B5]" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#2A2A2D] border border-[#3A3A3D] text-[#EAEAEA] rounded-lg focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent placeholder-[#666]"
              data-testid="search-input"
            />
          </div>

          {/* Filter Toggle Button (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden w-full flex items-center justify-center space-x-2 bg-[#2A2A2D] border border-[#3A3A3D] text-[#EAEAEA] px-4 py-3 rounded-lg hover:bg-[#3A3A3D]"
          >
            <SlidersHorizontal className="h-5 w-5" />
            <span>Filtros</span>
            {activeFiltersCount > 0 && (
              <span className="bg-[#C9A24D] text-[#0F0F10] text-xs font-bold px-2 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2">
              {filters.brand && (
                <span className="inline-flex items-center gap-1 bg-[#C9A24D]/20 text-[#C9A24D] px-3 py-1 rounded-full text-sm">
                  {filters.brand}
                  <button onClick={() => handleFilterChange('brand', '')} className="hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.condition && (
                <span className="inline-flex items-center gap-1 bg-[#C9A24D]/20 text-[#C9A24D] px-3 py-1 rounded-full text-sm">
                  {filters.condition === 'new' ? 'Novo' : 'Usado'}
                  <button onClick={() => handleFilterChange('condition', '')} className="hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {filters.color && (
                <span className="inline-flex items-center gap-1.5 bg-[#C9A24D]/20 text-[#C9A24D] px-3 py-1 rounded-full text-sm">
                  <span 
                    className="w-3 h-3 rounded-full border border-[#C9A24D]" 
                    style={{ backgroundColor: availableColors.find(c => c.id === filters.color)?.hex || '#333' }}
                  />
                  {selectedColorName}
                  <button onClick={() => handleFilterChange('color', '')} className="hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {(filters.minPrice || filters.maxPrice) && (
                <span className="inline-flex items-center gap-1 bg-[#C9A24D]/20 text-[#C9A24D] px-3 py-1 rounded-full text-sm">
                  {filters.minPrice && `R$ ${filters.minPrice}`} - {filters.maxPrice && `R$ ${filters.maxPrice}`}
                  <button onClick={() => { handleFilterChange('minPrice', ''); handleFilterChange('maxPrice', ''); }} className="hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className={`lg:block ${showFilters ? 'block' : 'hidden'} lg:col-span-1`}>
            <div className="bg-[#2A2A2D] border border-[#3A3A3D] rounded-lg shadow-lg p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-cinzel text-xl font-bold text-[#EAEAEA] uppercase tracking-wider">Filtros</h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#C9A24D] hover:text-[#E0B85C] font-medium uppercase tracking-wider"
                  >
                    Limpar
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {/* Brand Filter */}
                <div>
                  <label className="block text-sm font-semibold text-[#EAEAEA] mb-2 uppercase tracking-wider">
                    Marca
                  </label>
                  <select
                    value={filters.brand}
                    onChange={(e) => handleFilterChange('brand', e.target.value)}
                    className="w-full px-3 py-2 bg-[#1C1C1F] border border-[#3A3A3D] text-[#EAEAEA] rounded-lg focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent"
                    data-testid="brand-filter"
                  >
                    <option value="">Todas as marcas</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Color Filter */}
                {availableColors.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-[#EAEAEA] mb-3 uppercase tracking-wider">
                      Cor
                    </label>
                    <div className="flex flex-wrap gap-2" data-testid="color-filter">
                      {availableColors.map(color => {
                        const isSelected = filters.color === color.id;
                        const isLight = isLightColor(color.hex);
                        
                        return (
                          <button
                            key={color.id}
                            onClick={() => handleColorSelect(color.id)}
                            className={`
                              w-8 h-8 rounded-full relative transition-all duration-200
                              flex items-center justify-center
                              hover:scale-110 cursor-pointer
                              ${isSelected 
                                ? 'ring-2 ring-[#C9A24D] ring-offset-2 ring-offset-[#2A2A2D]' 
                                : 'ring-1 ring-[#3A3A3D] hover:ring-[#666]'
                              }
                            `}
                            style={{ backgroundColor: color.hex }}
                            title={color.name}
                            data-testid={`color-filter-${color.id}`}
                            aria-label={`Filtrar por ${color.name}`}
                            aria-pressed={isSelected}
                          >
                            {isSelected && (
                              <Check 
                                className={`h-4 w-4 ${isLight ? 'text-gray-800' : 'text-white'}`}
                                strokeWidth={3}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {filters.color && (
                      <p className="text-[#B5B5B5] text-xs mt-2">
                        Filtrando por: {selectedColorName}
                      </p>
                    )}
                  </div>
                )}

                {/* Condition Filter */}
                <div>
                  <label className="block text-sm font-semibold text-[#EAEAEA] mb-2 uppercase tracking-wider">
                    Condição
                  </label>
                  <select
                    value={filters.condition}
                    onChange={(e) => handleFilterChange('condition', e.target.value)}
                    className="w-full px-3 py-2 bg-[#1C1C1F] border border-[#3A3A3D] text-[#EAEAEA] rounded-lg focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent"
                    data-testid="condition-filter"
                  >
                    <option value="">Todos</option>
                    <option value="new">Novo</option>
                    <option value="used">Usado</option>
                  </select>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-semibold text-[#EAEAEA] mb-2 uppercase tracking-wider">
                    Faixa de Preço
                  </label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder="Preço mínimo"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="w-full px-3 py-2 bg-[#1C1C1F] border border-[#3A3A3D] text-[#EAEAEA] rounded-lg focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent placeholder-[#666]"
                      data-testid="min-price-filter"
                    />
                    <input
                      type="number"
                      placeholder="Preço máximo"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="w-full px-3 py-2 bg-[#1C1C1F] border border-[#3A3A3D] text-[#EAEAEA] rounded-lg focus:ring-2 focus:ring-[#C9A24D] focus:border-transparent placeholder-[#666]"
                      data-testid="max-price-filter"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-[#B5B5B5]">
                {loading ? 'Carregando...' : `${products.length} produtos encontrados`}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="bg-[#2A2A2D] rounded-lg shadow-md h-96 animate-pulse"></div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map(product => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    highlightColor={filters.color} // Passa a cor filtrada para highlight
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-[#2A2A2D] border border-[#3A3A3D] rounded-xl shadow-md">
                <X className="h-16 w-16 mx-auto text-[#666] mb-4" />
                <h3 className="text-xl font-semibold text-[#EAEAEA] mb-2">
                  Nenhum produto encontrado
                </h3>
                <p className="text-[#B5B5B5] mb-6">
                  {filters.color 
                    ? `Não encontramos produtos na cor ${selectedColorName}. Tente outra cor.`
                    : 'Tente ajustar seus filtros de busca'
                  }
                </p>
                <button
                  onClick={clearFilters}
                  className="bg-[#C9A24D] hover:bg-[#E0B85C] text-[#0F0F10] px-6 py-2 rounded-lg font-bold uppercase tracking-wider transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function para determinar se uma cor é clara
const isLightColor = (hex) => {
  if (!hex) return false;
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substr(0, 2), 16);
  const g = parseInt(cleanHex.substr(2, 2), 16);
  const b = parseInt(cleanHex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

export default Catalog;
